import type { IncidentListSortKey } from '@prerender/diagnostics-api';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  nextSort,
  parseIncidentListParams,
  serialiseIncidentListParams,
  type IncidentListParams,
} from './incidentListParams';

export type IncidentListFilterPatch = Partial<Omit<IncidentListParams, 'page' | 'sort'>>;

export type IncidentListParamsState = {
  params: IncidentListParams;
  applyFilters: (patch: IncidentListFilterPatch) => void;
  toggleSort: (key: IncidentListSortKey) => void;
  goToPage: (page: number, options?: { replace: boolean }) => void;
};

export function useIncidentListParams(): IncidentListParamsState {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = parseIncidentListParams(searchParams);

  const applyFilters = useCallback(
    (patch: IncidentListFilterPatch): void => {
      setSearchParams(serialiseIncidentListParams({ ...params, ...patch, page: 1 }), { replace: true });
    },
    [params, setSearchParams]
  );

  const toggleSort = useCallback(
    (key: IncidentListSortKey): void => {
      setSearchParams(serialiseIncidentListParams({ ...params, sort: nextSort(params.sort, key), page: 1 }), {
        replace: true,
      });
    },
    [params, setSearchParams]
  );

  const goToPage = useCallback(
    (page: number, options: { replace: boolean } = { replace: false }): void => {
      setSearchParams(serialiseIncidentListParams({ ...params, page }), options);
    },
    [params, setSearchParams]
  );

  return { params, applyFilters, toggleSort, goToPage };
}
